// frontend/src/slices/swapApiSlice.js
import { apiSlice } from './apiSlice';
 
const SWAPS_URL = '/api/swaps';
 
export const swapApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // My shifts eligible for a swap request, within a date range
    getMySwapEligibleShifts: builder.query({
      query: ({ startDate, endDate }) => ({
        url: `${SWAPS_URL}/my-shifts`,
        params: { startDate, endDate },
      }),
      providesTags: ['SwapEligibleShifts'],
    }),
 
    // Eligible candidates for a specific shift
    getSwapCandidates: builder.query({
      query: (shiftId) => ({
        url: `${SWAPS_URL}/candidates/${shiftId}`,
      }),
    }),
 
    // Create a swap request against a pool of candidates
    createSwapRequest: builder.mutation({
      query: (data) => ({
        url: SWAPS_URL,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['SwapEligibleShifts', 'MySwapRequests', 'MySentSwapRequests'],
    }),
 
    // Swap requests where I'm a candidate and it's still open
    getMySwapRequests: builder.query({
      query: () => ({
        url: `${SWAPS_URL}/my-requests`,
      }),
      providesTags: ['MySwapRequests'],
    }),
 
    // Swap requests I have ACCEPTED, any resulting status — shows where
    // things stand after accepting (PendingApproval / Approved / Rejected)
    getMyAcceptedSwapRequests: builder.query({
      query: () => ({
        url: `${SWAPS_URL}/accepted`,
      }),
      providesTags: ['MyAcceptedSwapRequests'],
    }),
 
    // Swap requests I have sent, any status (for the "My Sent Requests" tab)
    getMySentSwapRequests: builder.query({
      query: () => ({
        url: `${SWAPS_URL}/sent`,
      }),
      providesTags: ['MySentSwapRequests'],
    }),
 
    // Accept a swap request (first-to-accept-wins)
    acceptSwapRequest: builder.mutation({
      query: (id) => ({
        url: `${SWAPS_URL}/${id}/accept`,
        method: 'POST',
      }),
      invalidatesTags: ['MySwapRequests', 'SwapEligibleShifts', 'MyAcceptedSwapRequests'],
    }),
 
    // Cancel my own swap request (only while still PendingAcceptance)
    cancelSwapRequest: builder.mutation({
      query: (id) => ({
        url: `${SWAPS_URL}/${id}/cancel`,
        method: 'PUT',
      }),
      invalidatesTags: ['SwapEligibleShifts', 'MySwapRequests', 'MySentSwapRequests'],
    }),
 
    // ── Manager approval queue ────────────────────────────────────────────
    getSwapRequestsForApproval: builder.query({
      query: () => ({
        url: `${SWAPS_URL}/pending-approval`,
      }),
      providesTags: ['SwapApprovalQueue'],
    }),
 
    approveSwapRequest: builder.mutation({
      query: ({ id, comment }) => ({
        url: `${SWAPS_URL}/${id}/approve`,
        method: 'PUT',
        body: { comment },
      }),
      invalidatesTags: ['SwapApprovalQueue', 'SwapEligibleShifts', 'MySwapRequests', 'MySentSwapRequests', 'MyAcceptedSwapRequests'],
    }),
 
    rejectSwapRequest: builder.mutation({
      query: ({ id, comment }) => ({
        url: `${SWAPS_URL}/${id}/reject`,
        method: 'PUT',
        body: { comment },
      }),
      invalidatesTags: ['SwapApprovalQueue', 'SwapEligibleShifts', 'MySwapRequests', 'MySentSwapRequests', 'MyAcceptedSwapRequests'],
    }),
  }),
});
 
export const {
  useGetMySwapEligibleShiftsQuery,
  useGetSwapCandidatesQuery,
  useCreateSwapRequestMutation,
  useGetMySwapRequestsQuery,
  useGetMyAcceptedSwapRequestsQuery,
  useGetMySentSwapRequestsQuery,
  useAcceptSwapRequestMutation,
  useCancelSwapRequestMutation,
  useGetSwapRequestsForApprovalQuery,
  useApproveSwapRequestMutation,
  useRejectSwapRequestMutation,
} = swapApiSlice;